/**
 * A phone registration that could not finish now is tried again (2 Oct 2026).
 *
 * Found on the emulator during a slow hour (Google at ~500 ms a ping): the
 * app waits 15 s for the push token, and a token that came later, or an API
 * it could not reach, left the phone off InGo's list until the next app start
 * or sign-in. A rider on a slow line then got offers only with the app open.
 *
 * Only what may succeed later is retried (a late token, a network or server
 * failure); a refused permission, push turned off or a browser is not. Each
 * attempt decides for itself whether it still applies (the same person signed
 * in): the guard is in the attempt, so a sign-out between two tries stops the
 * chain without anyone having to cancel it.
 */

/** 30 s, 2 min, 10 min; after that the next app start or sign-in tries again. */
export const RETRY_DELAYS_MS = [30000, 120000, 600000];

const chains = new Map();

/**
 * Try `attempt` again later, unless a chain for `key` already waits.
 *
 * @param {string} key one chain per person and realm, e.g. `rider:42`
 * @param {() => Promise<'stored' | 'retry' | 'stop'>} attempt
 * @param {{ delays?: number[], schedule?: (fn: () => void, ms: number) => unknown }} [options]
 * @returns {boolean} true when a chain was started, false when one already waits
 */
export function retryRegistration(key, attempt, { delays = RETRY_DELAYS_MS, schedule = (fn, ms) => setTimeout(fn, ms) } = {}) {
  if (chains.has(key)) {
    console.info(`[pushRetry] ${key}: a retry already waits`);
    return false;
  }
  let step = 0;
  const next = () => {
    if (step >= delays.length) {
      chains.delete(key);
      console.warn(`[pushRetry] ${key}: still not registered after ${delays.length} tries; the next app start or sign-in tries again`);
      return;
    }
    const wait = delays[step];
    step += 1;
    chains.set(key, true);
    schedule(async () => {
      let outcome;
      try {
        outcome = await attempt();
      } catch (e) {
        outcome = 'retry';
        console.warn(`[pushRetry] ${key}: try ${step} threw ${JSON.stringify({ message: e?.message || String(e) })}`);
      }
      console.info(`[pushRetry] ${key}: try ${step} ${JSON.stringify({ outcome })}`);
      if (outcome === 'retry') {
        next();
      } else {
        chains.delete(key);
      }
    }, wait);
  };
  console.info(`[pushRetry] ${key}: not registered yet, trying again in ${delays[0] / 1000} s`);
  next();
  return true;
}

/** Tests only: forget every waiting chain. */
export function resetRetriesForTests() {
  chains.clear();
}
